import { z } from 'zod';
/**
 * Oferty: słowniki, stany i obliczenia kwot (docs/02 rozdz. 5a i 6, założenia Z-11…Z-17).
 * Kwoty liczone w groszach (liczby całkowite), ilości w tysięcznych — bez błędów zaokrągleń liczb zmiennoprzecinkowych.
 */

export const CATALOG_KINDS = { urzadzenie: 'Urządzenie', material: 'Materiał', usluga: 'Usługa', pakiet: 'Pakiet' } as const;
export type CatalogKind = keyof typeof CATALOG_KINDS;

/** Nazwa pozycji zaczyna się od usługi (montaż, transport…) — to nie jest urządzenie, nawet gdy wymienia urządzenie. */
const SERVICE_PREFIX = /^(montaż|montaz|demontaż|demontaz|instalacj\p{L}*|wykonanie|usług\p{L}*|uslug\p{L}*|uruchomienie|serwis\p{L}*|przegląd\p{L}*|przeglad\p{L}*|transport|dojazd|projekt\p{L}*|robocizna|podłączenie|podlaczenie|dostaw\p{L}*|konfiguracja|pomiar\p{L}*|zgłoszenie|zgloszenie|dokumentacja|rabat|opłat\p{L}*|oplat\p{L}*)(?![\p{L}\d])/iu;
/** Słowa wskazujące urządzenie z numerem seryjnym (magazyn energii, falownik, pompa ciepła, klimatyzator…). */
const DEVICE_WORDS =
  /(?<![\p{L}\d])(magazyn|falownik|inwerter|pomp[ayę]|klimatyzator|jednostk\p{L}* (wewn|zewn)|kocioł|kotł|kociol|kotl|rekuperat\p{L}*|central\p{L}* (rekuper|wentyl|went\.)|klimakonwektor|agregat|kolektor\p{L}* (słon|slon)|zasobnik|bufor|wallbox|ładowark|ladowark|optymalizator|moduł\p{L}* (pv|fotowolt)|modul\p{L}* (pv|fotowolt)|panel\p{L}* (pv|fotowolt)|bateri|akumulator)/iu;

/**
 * Podpowiedź oznaczenia „urządzenie” (P20): pozycja spoza katalogu, której nazwa wskazuje urządzenie z numerem seryjnym
 * (lista urządzeń w protokole odbioru, karta instalacji). Tylko podpowiedź — użytkownik może oznaczenie zmienić.
 */
/**
 * Pozycja wygląda na usługę (jednostka usł. / h albo nazwa zaczyna się od montażu, transportu, rabatu…) — nie trafia do potrzeb
 * materiałowych, gdy nie ma powiązania z katalogiem (QA 11.2026 P1).
 */
export function looksLikeService(name: string, unit: string): boolean {
  return unit === 'usł.' || unit === 'h' || SERVICE_PREFIX.test(name.trim());
}

export function looksLikeDevice(name: string): boolean {
  const n = name.trim();
  return !!n && !SERVICE_PREFIX.test(n) && DEVICE_WORDS.test(n);
}

export const UNITS = ['szt.', 'kpl.', 'm', 'm²', 'mb', 'h', 'usł.', 'kg', 'l'] as const;

/** Stawki VAT dostępne w ofertach. System nie rozstrzyga o stawce — potwierdza ją użytkownik (Z-15). */
export const VAT_RATES = [23, 8, 5, 0] as const;
export type VatRate = (typeof VAT_RATES)[number];

/** Forma podpisu umowy ustalona z klientem w ofercie („Ustalenia do umowy”, QA P33). */
export const CONTRACT_SIGN_FORMS = {
  papierowy: 'Podpis papierowy (w biurze albo u klienta)',
  elektroniczny: 'Podpis elektroniczny (link e-mailem)',
} as const;
export type ContractSignForm = keyof typeof CONTRACT_SIGN_FORMS;

export const VAT_PROFILES = {
  '8': '8% — budownictwo mieszkaniowe (do potwierdzenia)',
  '23': '23% — stawka podstawowa',
  wlasne: 'Stawki z katalogu',
} as const;
export type VatProfile = keyof typeof VAT_PROFILES;

/**
 * Domyślny profil VAT nowej oferty (QA P6): same zakresy usługowe (rodzaj branży „usluga” — audyt, świadectwo, pomiary) — 23%;
 * inaczej 8% dla domu jednorodzinnego / mieszkania, 23% dla pozostałych budynków. Stawkę potwierdza przygotowujący ofertę.
 */
export function defaultVatProfile(buildingType: string | null | undefined, tradeKinds: string[]): VatProfile {
  if (tradeKinds.length > 0 && tradeKinds.every((k) => k === 'usluga')) return '23';
  return buildingType === 'dom_jednorodzinny' || buildingType === 'mieszkanie' ? '8' : '23';
}

/** Informacja przy wyjątku rabatowym (QA P18): pozycje bez kosztu zakupu — marży nie da się ocenić; null przy 0. */
export function missingCostNote(n: number): string | null {
  return n > 0 ? `Brak kosztu w ${n} ${n === 1 ? 'pozycji' : 'pozycjach'} — marży nie da się ocenić` : null;
}

/** „1 pozycja”, „2 pozycje”, „5 pozycji”. */
export function positionsPl(n: number): string {
  const d = n % 10;
  const t = n % 100;
  return `${n} ${n === 1 ? 'pozycja' : d >= 2 && d <= 4 && (t < 12 || t > 14) ? 'pozycje' : 'pozycji'}`;
}

/** QA8 P3: ostrzeżenie w oknie wydania — pozycje bez kosztu, marża liczona tylko z pozycji z kosztem; null przy 0. */
export function marginIncompleteNote(n: number): string | null {
  return n > 0 ? `${positionsPl(n)} bez kosztu — marża niepełna (liczona tylko z pozycji z kosztem)` : null;
}

/**
 * QA8 P6: zwięzły opis przekroczeń progów do tytułu zadania „Zatwierdź wyjątek…”. Wpisy „Wariant: rabat 18% (próg 10%)”
 * z identycznym przekroczeniem w kilku wariantach łączy w „rabat 18% (próg 10%) w 2 wariantach”.
 */
export function exceptionSummary(issues: string[]): string {
  // przekroczenia każdego wariantu, potem warianty z identyczną listą przekroczeń razem
  const byVariant = new Map<string, string[]>();
  for (const s of issues) {
    const m = /^(.*): ((?:rabat|marża) .*)$/.exec(s);
    const name = m ? m[1]! : '';
    byVariant.set(name, [...(byVariant.get(name) ?? []), m ? m[2]! : s]);
  }
  const groups = new Map<string, string[]>();
  for (const [name, whats] of byVariant) {
    const what = whats.join(' i ');
    groups.set(what, [...(groups.get(what) ?? []), name]);
  }
  return [...groups.entries()]
    .map(([what, names]) => (names.length > 1 ? `${what} w ${names.length} wariantach` : names[0] ? `${what} w wariancie „${names[0]}”` : what))
    .join(', ');
}

/** Ostrzeżenie bez blokady (docs/09 §3.5): stawka 8% w ofercie obejmującej zakres usługowy. */
export function serviceVatWarning(serviceTradeNames: string[], usesEightPct: boolean): string | null {
  if (!usesEightPct || serviceTradeNames.length === 0) return null;
  return `Stawka 8% przy zakresie usługowym (${serviceTradeNames.join(', ')}) — usługi takie jak audyt, świadectwo czy pomiary mają zwykle 23%. Sprawdź stawkę przed wydaniem.`;
}

export const ATTACHMENT_KINDS = { zdjecie: 'Zdjęcie', karta: 'Karta katalogowa', dtr: 'Dokumentacja techniczna', inne: 'Inny' } as const;
export type AttachmentKind = keyof typeof ATTACHMENT_KINDS;

export const PRESENTATIONS = {
  szczegolowa: 'Szczegółowa — pozycje z cenami',
  cena_pakietu: 'Cena całości — pozycje bez cen',
} as const;
export type Presentation = keyof typeof PRESENTATIONS;

export const VERSION_STATUSES = {
  roboczy: 'Robocza',
  wydany: 'Wydana',
  zaakceptowany: 'Zaakceptowana',
  zastapiony: 'Zastąpiona',
  wygasly: 'Wygasła',
  wycofany: 'Wycofana',
  odrzucony: 'Odrzucona przez klienta',
  do_zmiany: 'Klient prosi o zmiany',
} as const;

/** Sposób przekazania wydanej oferty klientowi (wydanie to stan, przekazanie — zdarzenie). */
export const OFFER_DELIVERY_METHODS = { osobiscie: 'Osobiście', email: 'E-mailem', link: 'Link do oferty', poczta: 'Pocztą', inny: 'Inaczej' } as const;
export type OfferDeliveryMethod = keyof typeof OFFER_DELIVERY_METHODS;

/** Jak klient przekazał decyzję zanotowaną przez biuro. */
export const DECISION_METHODS = { telefon: 'Telefonicznie', email: 'E-mailem', papier: 'Na piśmie', osobiscie: 'Osobiście', link: 'Na stronie z linkiem' } as const;
export type DecisionMethod = keyof typeof DECISION_METHODS;

/** Powody odrzucenia oferty przez klienta (strona z linkiem). */
export const REJECTION_REASONS = {
  cena: 'Za wysoka cena',
  zakres: 'Zakres nie odpowiada moim potrzebom',
  termin: 'Termin realizacji mi nie odpowiada',
  inna_oferta: 'Wybieram inną ofertę',
  rezygnacja: 'Rezygnuję z inwestycji',
  inny: 'Inny powód',
} as const;
export type RejectionReason = keyof typeof REJECTION_REASONS;
export type VersionStatus = keyof typeof VERSION_STATUSES;

export const ACCEPTANCE_METHODS = { link: 'Link (akceptacja online)', telefon: 'Telefonicznie', papier: 'Na papierze', email: 'E-mailem', ekran: 'Podpis na ekranie (w terenie)' } as const;
export type AcceptanceMethod = keyof typeof ACCEPTANCE_METHODS;

export interface Discount {
  type: 'procent' | 'kwota';
  /** procent (0–100) albo kwota w groszach */
  value: number;
}

export interface CalcItem {
  /** ilość — liczba z maks. 3 miejscami po przecinku */
  quantity: number;
  unitPriceGr: number;
  /** cena katalogowa w chwili dodania (do liczenia rabatu względem cennika); dla pozycji własnych = cena */
  listPriceGr: number;
  discount: Discount | null;
  vatRate: number;
  /** koszt jednostkowy (cena zakupu) — null, gdy nieznany */
  purchasePriceGr: number | null;
}

export interface ItemTotal {
  /** wartość netto po rabacie pozycji i rozłożonym rabacie wariantu */
  netGr: number;
  /** wartość netto przed rabatem wariantu (po rabacie pozycji) */
  netBeforeVariantGr: number;
}

export interface VariantTotals {
  items: ItemTotal[];
  listGr: number;
  netGr: number;
  vatGr: number;
  grossGr: number;
  vatByRate: { rate: number; netGr: number; vatGr: number }[];
  /** łączny rabat względem cen katalogowych, % */
  discountPct: number;
  costGr: number | null;
  /** liczba pozycji bez ceny zakupu (marża wtedy niepełna) */
  missingCost: number;
  marginPct: number | null;
}

/** Cena jednostkowa brutto z netto (podgląd w ofercie) i netto z wpisanej ceny brutto (oferta liczy od netto). */
export const unitGrossGr = (netGr: number, vatRate: number) => Math.round((netGr * (100 + vatRate)) / 100);
export const unitNetFromGrossGr = (grossGr: number, vatRate: number) => Math.round((grossGr * 100) / (100 + vatRate));

const lineGr = (quantity: number, priceGr: number) => Math.round((Math.round(quantity * 1000) * priceGr) / 1000);

/** Rozkłada kwotę proporcjonalnie do wag tak, by suma części była dokładnie równa kwocie (metoda największych reszt). */
export function distribute(amount: number, weights: number[]): number[] {
  const total = weights.reduce((a, b) => a + b, 0);
  if (total <= 0 || amount === 0) return weights.map(() => 0);
  const raw = weights.map((w) => (amount * w) / total);
  const base = raw.map((r) => Math.floor(r));
  let rest = amount - base.reduce((a, b) => a + b, 0);
  const order = raw.map((r, i) => ({ i, frac: r - Math.floor(r) })).sort((a, b) => b.frac - a.frac);
  for (const o of order) {
    if (rest <= 0) break;
    base[o.i]!++;
    rest--;
  }
  return base;
}

/**
 * Sumy wariantu: rabat pozycji (% lub kwota), rabat wariantu rozłożony proporcjonalnie na pozycje (dla poprawnego VAT),
 * VAT liczony od sumy netto w obrębie stawki (jak na fakturze — Z-16), marża = (netto − koszt) / netto.
 */
export function computeVariant(items: CalcItem[], variantDiscount: Discount | null): VariantTotals {
  const afterItem = items.map((it) => {
    const base = lineGr(it.quantity, it.unitPriceGr);
    if (!it.discount || it.discount.value <= 0) return base;
    const d = it.discount.type === 'procent' ? Math.round((base * Math.min(it.discount.value, 100)) / 100) : Math.min(it.discount.value, base);
    return base - d;
  });
  const subtotal = afterItem.reduce((a, b) => a + b, 0);
  let variantDiscountGr = 0;
  if (variantDiscount && variantDiscount.value > 0) {
    variantDiscountGr =
      variantDiscount.type === 'procent' ? Math.round((subtotal * Math.min(variantDiscount.value, 100)) / 100) : Math.min(variantDiscount.value, subtotal);
  }
  const shares = distribute(variantDiscountGr, afterItem);
  const nets = afterItem.map((n, i) => n - shares[i]!);

  const byRate = new Map<number, number>();
  items.forEach((it, i) => byRate.set(it.vatRate, (byRate.get(it.vatRate) ?? 0) + nets[i]!));
  const vatByRate = [...byRate.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([rate, netGr]) => ({ rate, netGr, vatGr: Math.round((netGr * rate) / 100) }));

  const netGr = nets.reduce((a, b) => a + b, 0);
  const vatGr = vatByRate.reduce((a, b) => a + b.vatGr, 0);
  const listGr = items.reduce((a, it) => a + lineGr(it.quantity, it.listPriceGr), 0);
  const missingCost = items.filter((it) => it.purchasePriceGr === null).length;
  const costGr = items.length && missingCost < items.length ? items.reduce((a, it) => a + (it.purchasePriceGr === null ? 0 : lineGr(it.quantity, it.purchasePriceGr)), 0) : null;
  // QA8 P3: marża tylko z pozycji z kosztem (jak w raportach marży) — pozycja bez kosztu nie podnosi marży
  const coveredNetGr = items.reduce((a, it, i) => a + (it.purchasePriceGr === null ? 0 : nets[i]!), 0);

  return {
    items: nets.map((n, i) => ({ netGr: n, netBeforeVariantGr: afterItem[i]! })),
    listGr,
    netGr,
    vatGr,
    grossGr: netGr + vatGr,
    vatByRate,
    discountPct: listGr > 0 ? Math.round(((listGr - netGr) / listGr) * 10000) / 100 : 0,
    costGr,
    missingCost,
    marginPct: costGr !== null && coveredNetGr > 0 ? Math.round(((coveredNetGr - costGr) / coveredNetGr) * 10000) / 100 : null,
  };
}

/**
 * Cena sprzedaży netto dla pożądanej marży (marża liczona od ceny sprzedaży, jak w ofertach i zestawieniach):
 * sprzedaż = zakup / (1 − marża). 25% marży przy zakupie 100 zł → 133,33 zł. Marża ≥ 100% → null (nie da się).
 */
export function salePriceForMargin(purchaseGr: number, marginPct: number): number | null {
  if (!Number.isFinite(purchaseGr) || !Number.isFinite(marginPct) || marginPct >= 100) return null;
  return Math.round(purchaseGr / (1 - marginPct / 100));
}

/** Zaokrąglenie ceny sprzedaży wyliczanej z reguły marży (zawsze w górę — marża nie spada poniżej docelowej). */
export const PRICE_ROUNDINGS = { brak: 'bez zaokrąglania', '1': 'w górę do 1 zł', '10': 'w górę do 10 zł', '100': 'w górę do 100 zł' } as const;
export type PriceRounding = keyof typeof PRICE_ROUNDINGS;

/** Cena sprzedaży z reguły pozycji katalogu: cena zakupu + docelowa marża, zaokrąglona w górę. */
export function ruleSalePrice(purchaseGr: number, marginPct: number, rounding: PriceRounding): number | null {
  const raw = salePriceForMargin(purchaseGr, marginPct);
  if (raw === null) return null;
  const step = rounding === 'brak' ? 1 : Number(rounding) * 100;
  return Math.ceil(raw / step) * step;
}

/** Marża % z cen netto: (sprzedaż − zakup) / sprzedaż, zaokrąglona do 0,1 pkt; null bez cen. */
export function marginPctOf(saleGr: number | null, purchaseGr: number | null): number | null {
  if (saleGr === null || purchaseGr === null || saleGr <= 0) return null;
  return Math.round(((saleGr - purchaseGr) / saleGr) * 1000) / 10;
}

/** Narzut % (od ceny zakupu): (sprzedaż − zakup) / zakup — do podpowiedzi obok marży. */
export function markupPctOf(saleGr: number | null, purchaseGr: number | null): number | null {
  if (saleGr === null || purchaseGr === null || purchaseGr <= 0) return null;
  return Math.round(((saleGr - purchaseGr) / purchaseGr) * 1000) / 10;
}

/** Kwota w groszach → „12 345,67 zł”. */
export function formatMoney(gr: number, withCurrency = true): string {
  // grupowanie także liczb 4-cyfrowych — spójne kolumny kwot w tabelach
  const s = new Intl.NumberFormat('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: 'always' }).format(gr / 100);
  return withCurrency ? `${s} zł` : s;
}

/** „1234,56” / „1 234.56” → grosze; null, gdy niepoprawne. */
export function parseMoney(text: string): number | null {
  const t = text.replace(/\s|zł/g, '').replace(',', '.');
  if (!/^-?\d+(\.\d{0,2})?$/.test(t)) return null;
  return Math.round(Number(t) * 100);
}

/** numeric z bazy („1234.50”) → grosze. */
export const toGr = (v: string | number | null | undefined): number | null => (v === null || v === undefined ? null : Math.round(Number(v) * 100));
/** grosze → numeric do bazy („1234.50”). */
export const fromGr = (gr: number | null): string | null => (gr === null ? null : (gr / 100).toFixed(2));

/** Numer wersji: OF/A/2026/0012 (v1), OF/A/2026/0012-v2 … */
export const versionNumber = (offerNumber: string, versionNo: number) => (versionNo > 1 ? `${offerNumber}-v${versionNo}` : offerNumber);

// ── decyzje klienta rejestrowane przez biuro (docs/10 §3.3) ──

export const offerDeliverySchema = z.object({
  method: z.enum(Object.keys(OFFER_DELIVERY_METHODS) as [OfferDeliveryMethod, ...OfferDeliveryMethod[]]),
  date: z.string().date(),
  note: z.string().trim().max(500).default(''),
});
export const offerOfficeRejectionSchema = z.object({
  personName: z.string().trim().min(2, 'Kto przekazał decyzję').max(200),
  reasonCode: z.enum(['cena', 'zakres', 'termin', 'inna_oferta', 'rezygnacja', 'inny']),
  reason: z.string().trim().max(1000).default(''),
  method: z.enum(['telefon', 'email', 'papier', 'osobiscie']),
});
export const offerChangesSchema = z.object({ note: z.string().trim().min(3, 'Opisz, czego oczekuje klient').max(2000) });
